import {DomainKeys, Peer, State} from '../../state/types/state.js';
import * as agm from '../../state/types/agm.js';
import type { AgmDomain, AgmPeer, Invocation, InvocationId } from '../../state/types/agm.js';
import {PeerId, RequestId, Source} from '../../state/types/common.js';
import {error, isLocalSource, isRemoteSource, unicast} from '../../common/messages.js';
import {agmDomainUri, agmInvocationFailure} from './constants.js';
import {reason, requestToReason} from '../../reason.js';
import {byId, byIdInDomain, byIdInDomainThrows, isLocalPeer, setPeer, updatePeer} from '../../state/peers.js';
import {Draft, produce} from 'immer';
import {HandleReturnType} from '../../domain.js';
import * as msg from './messages.js';
import {nodeToAddress, peerToAddress} from '../../address.js';
import {nodeId, requestId} from '../../common/ids.js';
import {CallRequest, RemoteCallRequest, YieldRequest} from './types/requests.js';
import {validate} from './utilities.js';
import {Call} from './types/messages.js';
import {DomainMessage} from '../../node.js';


/**
 * Records and generates a method invocation.
 *
 *   If the callee is local - direct invocation message. Otherwise, we'll broadcast the call request and it
 *   will be processed by the node that owns the callee peer
 */
function invoke(state: State, request: RemoteCallRequest, callee: Peer): HandleReturnType {
    const {
        request_id: requestId,
        peer_id: peerId,
        server_id: serverId,
        invocation_id: invocationId,
        method_id: methodId,
        arguments: args,
        arguments_kv: argsKv,
        context
    } = request;
    callee = produce(callee, (c) => {
        const d: Draft<agm.AgmDomain> = c[DomainKeys.Agm]!;
        d.invocations = d.invocations || {};
        d.invocations[invocationId] = {caller: peerId, method_id: methodId, request_id: requestId};
    });
    const isRemoteCallee = !isLocalPeer(callee);
    return [
        setPeer(state, serverId, callee),
        isRemoteCallee
            ? [unicast(peerToAddress(nodeId(state.ids), peerId), nodeToAddress(callee.source['node']), {domain: agmDomainUri, type: 'call', ...request} as Call)]
            : [msg.invoke(callee.source, invocationId, serverId, methodId, peerId, args, argsKv, context)]];
}

function call(state: State, request: CallRequest, invocationId: string, callee: Peer, caller: Peer): HandleReturnType {
    const {request_id: requestId, peer_id: peerId, server_id: serverId, method_id: methodId} = request;
    // todo large msg
    // todo log action


    caller = produce(caller, (c) => {
        const d: Draft<AgmDomain> = c[DomainKeys.Agm]!;
        d.calls = d.calls || {};
        d.calls[requestId] = {callee: serverId, method_id: methodId, invocation_id: invocationId};
    });

    // todo feature metric

    return invoke(
        setPeer(state, peerId, caller),
        {...request, invocation_id: invocationId},
        peerId === serverId ? caller : callee);
}

/**
 * Processes a remote call request. Ignored if the callee is not owned by this node
 */
function remoteCall(state: State, source: Source, request: RemoteCallRequest) : HandleReturnType {
    const {request_id: requestId, peer_id: peerId, server_id: serverId, invocation_id: invocationId} = request;
    const callee = byIdInDomain(state, serverId, DomainKeys.Agm);
    if (isLocalPeer(callee)) {
        return call(state, request, invocationId, callee!, byIdInDomainThrows(state, peerId, DomainKeys.Agm));
    }
    return [state, []];
}

/**
 * Processes a local call request
 */
function localCall(state: State, source: Source, request: CallRequest): HandleReturnType {
    const {peer_id: peerId, server_id: serverId} = request;
    const callee = byIdInDomainThrows(state, serverId, DomainKeys.Agm);
    const caller = byIdInDomainThrows(state, peerId, DomainKeys.Agm);
    const [newIds, invocationId] = requestId(state.ids);
    validate(caller, callee, request);
    return call({...state, ids: newIds}, request, invocationId, callee, caller)
}

export function forwardCall(state: State, source: Source, request: CallRequest | (RemoteCallRequest & DomainMessage)): HandleReturnType {
    return isRemoteSource(source) ? remoteCall(state, source, request as (RemoteCallRequest & DomainMessage)) : localCall(state, source, request);
}

/* Cancels any pending invocations made by caller-id to callee-id. Returns a [state [message]]*/
export function cancelInvocations(state: State, receiver, caller: Peer, callee: AgmPeer): HandleReturnType {
    const calleeId = callee.id;
    const [removed, remaining] = Object.entries(caller[DomainKeys.Agm]?.calls ?? {}).reduce(([removedAgg, remainingAgg], [requestId, call]) => {
        (call.callee === calleeId ? removedAgg : remainingAgg).push([requestId, call]);
        return [removedAgg, remainingAgg];
    }, [new Array<[RequestId, agm.Call]>(), new Array<[RequestId, agm.Call]>()]);

    function errorFn([requestId, ]:[string, agm.Call]) {
        return error(agmDomainUri, receiver, requestId, caller.id, reason(agmInvocationFailure, 'Peer has left while waiting for result'))
    }

    if (removed.length > 0) {

        return [updatePeer(state, caller.id, (p: Draft<Peer>) => {
            const domain = p[DomainKeys.Agm]!;
            if (remaining.length > 0) {
                domain.calls = remaining.reduce((agg, [requestId, call]) => {
                    agg = produce(agg, (a: Draft<agm.Calls>) => {
                        a[requestId] = call;
                    });
                    return agg;
                }, {} as agm.Calls);
            } else {
                delete domain.calls;
            }
        }), isLocalPeer(caller) ? removed.map(errorFn) : []];
    } else {
        return [state, []];
    }
}

function completeCall(state: State, calleeId: PeerId, invocation: Invocation, request, successFailure: { success?: { result }, failure? }): HandleReturnType {
    const {request_id: requestId, caller: callerId, method_id: methodId} = invocation;
    let caller = byId(state, callerId);
    if (caller) {
        caller = produce(caller, (c) => {
            if (c[DomainKeys.Agm]) {
                if (c[DomainKeys.Agm].calls) {
                    delete c[DomainKeys.Agm].calls[requestId];
                    if (Object.keys(c[DomainKeys.Agm].calls).length === 0) {
                        delete c[DomainKeys.Agm].calls;
                    }
                }
            }
        });
    }
    if (caller) {
        state = setPeer(state, callerId, caller);
        const callee = byId(state, calleeId);
        const method = callee?.[DomainKeys.Agm]?.methods?.[calleeId][methodId];
        const success = successFailure.success;
        const failure = successFailure.failure;
        if (isLocalPeer(caller)) {
            if (success) {
                // todo features
                // todo large msg
                // todo log action
                return [state, [msg.result(caller.source, requestId, callerId, success.result)]]
            } else {
                // todo features
                // todo log action
                return [state, [error(agmDomainUri, caller.source, requestId, callerId, requestToReason(failure), failure.context)]];
            }
        } else {
            return [state, [unicast(peerToAddress(nodeId(state.ids), calleeId), nodeToAddress(caller.source['node']), request)]];
        }
    }
    return [state, []];
}

/*Removes the call/invocation information from the caller and the callee.*/
export function completeInvocation(state: State, invocationId: InvocationId, peerId, request, successFailure): HandleReturnType | undefined {
    const callee = byId(state, peerId);
    const invocation: Invocation | undefined = callee?.[DomainKeys.Agm]?.invocations?.[invocationId];
    if (invocation) {
        state = setPeer(state, peerId, produce(callee!, (p: Draft<Peer>) => {
            if (p[DomainKeys.Agm]?.invocations) {
                delete p[DomainKeys.Agm].invocations[invocationId];
                if (Object.keys(p[DomainKeys.Agm].invocations).length === 0) {
                    delete p[DomainKeys.Agm].invocations;
                }
            }
        }));
        return completeCall(state, peerId, invocation, request, successFailure);
    }
}

function completeYield(state: State, source, request: YieldRequest): HandleReturnType {
    const {invocation_id: invocationId, peer_id: peerId} = request;
    byIdInDomainThrows(state, peerId, DomainKeys.Agm);
    return completeInvocation(state, invocationId, peerId, request, {success: request}) ?? [state, []];
}

function remoteYield(state, source, request): HandleReturnType {
    return completeYield(state, source, request);
}

function localYield(state, source, request): HandleReturnType {
    return completeYield(state, source, request);
}

export function forwardYield(state: State, source, request: YieldRequest): HandleReturnType {
    return isLocalSource(source) ? localYield(state, source, request) : remoteYield(state, source, request);
}
